// Study guides: plain, practical articles about preparing for trade exams. They
// are content in code (like faq.ts) so the app, the Cloudflare Worker (search
// tags, sitemap) and the tests all read the same text. To add a guide, add an
// entry here: it gets a page, search tags and a sitemap entry automatically.
//
// These are general study advice. They deliberately say nothing about any
// particular exam's content, pass mark or rules (those differ by trade and
// change); Flynt is welcome to review and sharpen them.
//
// Pictures: put the files in public/guide-images/ and set `image` on a guide
// (top of the page, the list and link previews) and/or on any section. A test
// checks that every picture named here exists.

/** A picture: a file in public/guide-images/ (e.g. '/guide-images/mock-exam.jpg') and a description for people who can't see it. */
export interface GuideImage {
  src: string;
  alt: string;
}

export interface GuideSection {
  /** Optional picture shown under the heading. */
  image?: GuideImage;
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface Guide {
  /** The address: /guides/<slug>. Lowercase letters, numbers and hyphens. */
  slug: string;
  title: string;
  /** One or two sentences for search results and the Guides list (about 150 characters). */
  description: string;
  /** The date it was last reviewed, YYYY-MM-DD. */
  updated: string;
  /** How long it takes to read, in minutes. */
  minutes: number;
  /** Optional main picture: shown at the top of the guide, on the list, and in link previews (ideally 1200x630). */
  image?: GuideImage;
  intro: string;
  sections: GuideSection[];
}

export const GUIDES: Guide[] = [
  {
    slug: "how-to-study-for-a-trade-exam",
    title: "How to study for a trade exam",
    description:
      "A simple, practical way to prepare for a trade exam: what to study first, how to practise, and how to know when you are ready.",
    updated: "2026-10-19",
    minutes: 5,
    image: {
      src: "/guide-images/Study-Session.webp",
      alt: "An apprentice studying a textbook",
    },
    intro:
      "Most people who struggle with a trade exam don't lack ability. They studied in a way that felt busy but didn't build recall. Here's an approach that works for most people, whatever the trade.",
    sections: [
      {
        heading: "Start with the syllabus, not the textbook",
        paragraphs: [
          "Find out exactly what the exam covers: the topics, how the questions are asked (multiple choice, calculations, scenarios) and how long you get. Your training provider or the exam body's website will tell you. Everything you do should map back to that list.",
        ],
      },
      {
        heading: "Find your weak spots early",
        paragraphs: [
          "Don't start at page one and read through. Do a set of practice questions first, with no preparation. It will be uncomfortable, but it shows you which topics cost you marks, and those are where your time pays off most.",
        ],
      },
      {
        heading: "Practise recalling, not rereading",
        paragraphs: [
          "Rereading notes feels productive but mostly builds familiarity. What builds memory is trying to recall an answer and then checking it. Practice questions do exactly that, especially when each answer comes with an explanation of why it is right.",
        ],
        bullets: [
          "After every question, read the explanation, including when you got it right. Sometimes you were right for the wrong reason.",
          "Write down the rule or fact you missed, in your own words, in one line.",
          "Come back to missed questions a few days later. If you can answer them cold, the learning has stuck.",
        ],
      },
      {
        heading: "Little and often beats cramming",
        paragraphs: [
          "Twenty to thirty minutes most days beats one long weekend session. Your brain consolidates what you study while you rest, so spreading it out gives you more for the same hours. A phone is handy for this: practise on a break, on the bus, or before bed.",
        ],
      },
      {
        heading: "Know when you are ready",
        paragraphs: [
          "Sit full, timed mock exams in the last couple of weeks. When you are passing them comfortably, with a margin above the pass mark, across more than one attempt, you are close. One good score can be luck; a run of them is readiness.",
        ],
      },
    ],
  },
  {
    slug: "how-to-get-the-most-from-practice-questions",
    title: "How to get the most from practice questions",
    description:
      "Practice questions are the fastest way to learn, if you use them well. How to review answers, track your weak topics and avoid memorising.",
    updated: "2026-10-19",
    minutes: 4,
    intro:
      "Practice questions are one of the best study tools there is, but only if you use them to learn rather than just to check a score. A few habits make the difference.",
    sections: [
      {
        heading: "Answer first, then read the explanation",
        paragraphs: [
          "Commit to an answer before you look. Guessing and then finding out you were wrong is a powerful way to remember. Peeking first only trains you to recognise the answer when you see it.",
        ],
      },
      {
        heading: "Study the explanations, not just the answers",
        paragraphs: [
          "The explanation is where the learning is. It tells you the rule, why the wrong options are wrong, and often a trap to watch for. A question you got right but can't explain is a question you might get wrong next time.",
        ],
      },
      {
        heading: "Flag what you are unsure about",
        paragraphs: [
          "Mark questions that you guessed, or got right with low confidence. Review your flagged questions regularly. They are a short, personal list of exactly what needs more work.",
        ],
      },
      {
        heading: "Don't memorise the questions",
        paragraphs: [
          "If you start recognising a question by its first few words, you are remembering the question, not the knowledge. The real exam will word things differently. Try to explain the answer in your own words, and use more than one question bank on the same topic if you can.",
        ],
      },
      {
        heading: "Look at your results by topic",
        paragraphs: [
          "An overall score hides the detail. Look at how you did topic by topic, and put your next sessions into the lowest ones. Improving a 50% topic to 80% is worth far more than polishing one you already know.",
        ],
      },
    ],
  },
  {
    slug: "how-to-use-a-mock-exam",
    title: "How to use a mock exam",
    description:
      "What a mock exam is for, when to sit one, and how to review it properly so that every attempt makes the real exam easier.",
    updated: "2026-10-19",
    minutes: 4,
    intro:
      "A mock exam isn't just a test. Used well, it's rehearsal for exam conditions and a map of what to study next.",
    sections: [
      {
        heading: "Make it feel like the real thing",
        bullets: [
          "Set the same time limit and number of questions as the real exam, as closely as you can.",
          "Sit it in one go, somewhere quiet, with your phone silenced.",
          "Only use what you will be allowed to use in the real exam (a calculator, a permitted reference).",
        ],
      },
      {
        heading: "When to sit one",
        paragraphs: [
          "Don't wait until you feel ready. Do one early, as a baseline, to see where you stand. Then sit mocks regularly through your study, and do a few full, timed ones in the last two or three weeks.",
        ],
      },
      {
        heading: "Review it properly",
        paragraphs: [
          "The review matters more than the score. Go back through every question you got wrong or guessed, and read the explanation. Then ask yourself why you got it wrong:",
        ],
        bullets: [
          "I didn't know the content. (Go back and study that topic.)",
          "I misread the question. (Slow down and underline key words.)",
          "I ran out of time. (Practise pacing.)",
          "I changed a right answer to a wrong one. (Trust your first instinct unless you have a clear reason.)",
        ],
      },
      {
        heading: "Don't read too much into one score",
        paragraphs: [
          "A single result can be a good or bad day. Look at the trend across several attempts and across topics. Steady improvement is what you are after.",
        ],
      },
    ],
  },
  {
    slug: "building-a-study-plan",
    title: "Building a study plan you will stick to",
    description:
      "How to turn the weeks before your exam into a realistic plan: set a date, split the topics, schedule short sessions and leave room for review.",
    updated: "2026-10-19",
    minutes: 5,
    intro:
      "A good plan is one you will actually follow. It should fit around work, family and a tired brain after a long day on the tools.",
    sections: [
      {
        heading: "Start from the exam date",
        paragraphs: [
          "Count the weeks you have. Then count the study sessions you can really manage each week, not the ones you would like to. Four honest sessions a week beat seven you will skip.",
        ],
      },
      {
        heading: "Split the topics across the weeks",
        paragraphs: [
          "List the exam topics, put the weakest and heaviest first, and give each a block of time. Leave the last two weeks for mixed practice, mock exams and revisiting weak spots rather than new material.",
        ],
      },
      {
        heading: "Keep sessions short and specific",
        paragraphs: [
          'Plan "30 minutes: 20 questions on earthing, then review the ones I missed" rather than "study electrical". A clear task is easier to start, and finishing it feels good.',
        ],
      },
      {
        heading: "Build in review",
        paragraphs: [
          "Set aside a little time each week to go back over earlier topics and flagged questions. Without review, you will forget much of what you learned in the first weeks by the time the exam arrives.",
        ],
      },
      {
        heading: "Make it a habit",
        bullets: [
          "Pick a regular time, such as after dinner or on the morning commute.",
          "Start small. Ten minutes still counts, and it keeps the streak alive.",
          "Track your progress, so you can see it building.",
          "If you miss a session, just do the next one. Do not try to catch up all at once.",
        ],
      },
    ],
  },
  {
    slug: "exam-day-checklist",
    title: "Exam day checklist",
    description:
      "What to sort the night before, what to take, and how to manage your time and nerves on the day of your trade exam.",
    updated: "2026-10-19",
    minutes: 3,
    intro:
      "By exam day the studying is done. The aim now is to arrive calm, prepared and ready to show what you know.",
    sections: [
      {
        heading: "The night before",
        bullets: [
          "Check the time, place and what you must bring, using the details from your exam provider.",
          "Pack your ID and anything you are allowed to take in (such as a calculator), and check that the batteries work.",
          "Do a light review, not a cram. Then stop, eat well and get a proper sleep.",
        ],
      },
      {
        heading: "On the day",
        bullets: [
          "Eat something and drink water. Arrive early, so you are not rushing.",
          "Read the instructions carefully, including how the exam is marked.",
          "Quickly look through the paper to see how it is laid out and how much time you have per question.",
        ],
      },
      {
        heading: "During the exam",
        bullets: [
          'Read each question twice. Underline the key words, especially "not", "except" and units.',
          "If a question stumps you, mark it, move on and come back. Do not let one question eat your time.",
          "With multiple choice, rule out the options you know are wrong first.",
          "Keep an eye on the clock, but don't panic about it. A steady pace beats a rushed one.",
          "If time allows, check your answers, especially calculations and units.",
        ],
      },
      {
        heading: "If your nerves kick in",
        paragraphs: [
          "Take a slow breath in and out, drop your shoulders, and bring your attention back to the question in front of you. Nerves mean you care; they do not mean you are unprepared.",
        ],
      },
    ],
  },
];

export const guideBySlug = (slug: string): Guide | undefined =>
  GUIDES.find((g) => g.slug === slug);

/** A guide's text with no markup, for the plain-HTML version crawlers see. */
export const guideText = (g: Guide): string =>
  [
    g.intro,
    ...g.sections.flatMap((s) => [
      s.heading,
      ...(s.paragraphs ?? []),
      ...(s.bullets ?? []),
    ]),
  ].join(" ");
